'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Button, Icon, IconComponent } from '@graftcode/design-system'

import { ERROR_BOUNDARY_TEST_IDS } from '@common/common-test-ids.const'

import styles from './ErrorBoundary.module.scss'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className={styles.container} data-testid={ERROR_BOUNDARY_TEST_IDS.root}>
          <IconComponent icon={Icon.ErrorCircleRegular} className={styles.icon} />
          <span className={styles.title}>Something went wrong</span>
          <span className={styles.message}>{this.state.error?.message || 'An unexpected error occurred'}</span>
          <Button variant="outlined" size="s" text="Try again" onClick={this.handleRetry} />
        </div>
      )
    }

    return this.props.children
  }
}
